/**
 * Draw an unread/mention badge onto the browser favicon (Telegram/Discord
 * style). Mentions show a loud red pill with a count; plain unread shows a
 * quiet accent dot ("you have new stuff"). Mirrors {@link installUnreadTitle}:
 * install once at boot, subscribe to the data store, repaint a data-url favicon
 * on change. Restores the static icon when everything is read.
 */
import { useDataStore } from '@/stores/dataStore';
import { readUnreadTotals } from './unreadTotals';

const BASE_HREF = '/favicon.png';

let baseImg: HTMLImageElement | null = null;
let baseReady = false;
// Last rendered "shape" so we don't regenerate a data-url on every store tick.
let lastKey = '';

function iconLink(): HTMLLinkElement {
  let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  return link;
}

function render(total: number, mentions: number): void {
  if (!baseReady || !baseImg) return;
  // Plain unread is a dot (no number), so its key is constant regardless of the
  // count; mentions encode the count (capped) so 1→2 repaints.
  const key = mentions > 0 ? `m${Math.min(mentions, 10)}` : total > 0 ? 'u' : '';
  if (key === lastKey) return;
  lastKey = key;

  const link = iconLink();
  if (!key) {
    link.href = BASE_HREF;
    return;
  }

  const S = 64;
  const canvas = document.createElement('canvas');
  canvas.width = S;
  canvas.height = S;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.drawImage(baseImg, 0, 0, S, S);

  if (mentions > 0) {
    const r = 21;
    const cx = S - r + 3;
    const cy = S - r + 3;
    ctx.beginPath();
    ctx.arc(cx, cy, r + 4, 0, Math.PI * 2);
    ctx.fillStyle = '#0b0b0d'; // dark ring so the badge reads off the icon
    ctx.fill();
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = '#f23f43';
    ctx.fill();
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 26px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(mentions > 9 ? '9+' : String(mentions), cx, cy + 1);
  } else {
    const r = 14;
    const cx = S - r - 2;
    const cy = S - r - 2;
    ctx.beginPath();
    ctx.arc(cx, cy, r + 4, 0, Math.PI * 2);
    ctx.fillStyle = '#0b0b0d';
    ctx.fill();
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = '#0485f7';
    ctx.fill();
  }

  link.href = canvas.toDataURL('image/png');
}

export function installFaviconBadge(): () => void {
  if (typeof document === 'undefined') return () => undefined;

  baseImg = new Image();
  baseImg.onload = () => {
    baseReady = true;
    lastKey = ' '; // force the first paint now that the base image is ready
    const { total, mentions } = readUnreadTotals();
    render(total, mentions);
  };
  baseImg.src = BASE_HREF;

  const apply = () => {
    const { total, mentions } = readUnreadTotals();
    render(total, mentions);
  };
  apply();
  return useDataStore.subscribe(apply);
}
